import { useMemo } from 'react';
import { filterKeysByTimeOfDay, type I18NLocale } from 'Locale';
import {
    type WeeklyVoiceLimitState,
    UI_CONTEXT_REVIEW_LESSON_AUDIO,
    useWeeklyVoiceTimeLimitState,
} from 'TutorBotConversation';
import { type LessonPlayerBotAudioUiContext } from 'TutorBotAiTutorChat';

function getIntroMessageLocales({
    uiContext,
    forceMorning,
}: {
    uiContext: LessonPlayerBotAudioUiContext;
    forceMorning: boolean;
}): I18NLocale[] {
    return [
        // To support storybook, we need the ability to turn off the dynamic response to the time of day
        forceMorning
            ? { key: 'good_morning' }
            : filterKeysByTimeOfDay([{ key: 'good_morning' }, { key: 'good_afternoon' }, { key: 'good_evening' }])[0],
        {
            key:
                uiContext === UI_CONTEXT_REVIEW_LESSON_AUDIO
                    ? 'the_session_will_use_review_this_lesson'
                    : 'the_session_will_use',
        },
        { key: 'just_find_a_quiet_spot' },
    ];
}

function getWelcomeBackIntroMessageLocales({
    weeklyVoiceTimeLimitState,
}: {
    weeklyVoiceTimeLimitState: WeeklyVoiceLimitState;
}): I18NLocale[] {
    const { usedMinutes, allowedMinutes, refreshHour, refreshDayOfWeek } = weeklyVoiceTimeLimitState;

    return [
        { key: 'welcome_back' },
        { key: 'you_have_used', params: { usedMinutes, allowedMinutes, refreshHour, refreshDayOfWeek } },
        { key: 'ready_to_review_find_a_spot' },
    ] as const;
}

export function useIntroMessageLocales({
    uiContext,
    forceMorning,
}: {
    uiContext: LessonPlayerBotAudioUiContext;
    forceMorning: boolean;
}): I18NLocale[] {
    const weeklyVoiceTimeLimitState = useWeeklyVoiceTimeLimitState();

    return useMemo(
        () =>
            weeklyVoiceTimeLimitState.usedMinutes > 0
                ? getWelcomeBackIntroMessageLocales({
                      weeklyVoiceTimeLimitState,
                  })
                : getIntroMessageLocales({ uiContext, forceMorning }),
        [uiContext, weeklyVoiceTimeLimitState, forceMorning],
    );
}
